import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon } from "@conuti-das/prince-ui";
import { Flex, princeIcon } from "@/ui-prince";
import { ActivityGuide, ActivityStream, buildTimeline } from "@/components/activity";
import type { ActivityBand, ActivityEntry } from "@/components/activity";
import { AutorunEditor } from "@/components/autoruns/AutorunEditor";
import { listAutoruns, type AutorunCfg } from "@/api/autoruns";
import { cancelJob, getQueue, type QueueSnapshot } from "@/api/jobs";
import { useQueue } from "@/hooks/useQueue";
import runsData from "@/data/generated/runs.json";
import type { Run } from "@/types";
import "@/components/activity/activity.css";

const RUNS = runsData as Run[];

/** Relativzeiten altern; einmal pro Minute reicht, um sie ehrlich zu halten. */
const CLOCK_MS = 60_000;
/** Die Queue pollt alle 3 s. Autoruns ändern sich selten — und heilen so von selbst. */
const AUTORUN_POLL_MS = 30_000;

/**
 * Aktivität — Runs, Queue und Autoruns auf EINER Zeitachse.
 *
 * Drei Quellen, drei Zeitformen, ein Strom (siehe `components/activity/timeline.ts`):
 * oben, was von selbst laufen wird (Autoruns als Vorschau nach vorn), in der
 * Mitte das Jetzt (Queue-Poll alle 3 s), darunter der Verlauf aus dem
 * Run-Journal. Nach unten wird die Zeit kleiner; die Naht zwischen Zukunft und
 * Vergangenheit ist das Jetzt-Band.
 *
 * **Auslösen und Anhalten gehören ins selbe Bild**: „Neuer Run" in der
 * Kopfzeile, die Autorun-Pflege am Zukunftsband — dort, wo das Ergebnis
 * auftauchen wird — und das Abbrechen am laufenden oder wartenden Job im
 * Jetzt-Band. Der Editor ist der bestehende `AutorunEditor`, nicht ein zweiter.
 *
 * **Toter Runner ist der Normalfall lokal** (`/api/queue` und `/api/autoruns`
 * antworten mit 500). Das ist kein Seitenfehler: die Run-Historie ist der
 * wertvollste Teil und kommt aus einer JSON-Datei. Statt eines roten Bands über
 * allem sagt jedes betroffene Band in einem Satz, was gerade fehlt — und die
 * Achse bleibt vollständig lesbar.
 */
export function ActivityPage() {
  const nav = useNavigate();
  const { data: polled, error: queueError } = useQueue(3000);

  const [autoruns, setAutoruns] = useState<AutorunCfg[] | null>(null);
  const [autorunError, setAutorunError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ initial: AutorunCfg | null } | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Sofort-Nachladen nach einem Abbruch: der 3-s-Poll holt es ohnehin nach,
  // aber eine Zeile, die nach dem Klick noch drei Sekunden „läuft" behauptet,
  // ist eine Lüge. Der nächste Poll überholt den Zwischenstand von selbst.
  const [fresh, setFresh] = useState<QueueSnapshot | null>(null);
  useEffect(() => { setFresh(null); }, [polled]);
  const queue = fresh ?? polled;

  const [cancelBusyKey, setCancelBusyKey] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const loadAutoruns = useCallback(async () => {
    try {
      setAutoruns(await listAutoruns());
      setAutorunError(null);
    } catch (e: any) {
      // Kein Aufschrei: der Runner ist lokal meistens aus. Die Meldung landet
      // im Zukunftsband, nicht über der Seite.
      setAutorunError(e?.message ?? String(e));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = () => { if (!cancelled) loadAutoruns(); };
    tick();
    const t = setInterval(tick, AUTORUN_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [loadAutoruns]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), CLOCK_MS);
    return () => clearInterval(t);
  }, []);

  const timeline = useMemo(
    () => buildTimeline({ runs: RUNS, queue, autoruns, now }),
    [queue, autoruns, now],
  );

  const openAutorun = (entry: ActivityEntry) => {
    const cfg = (autoruns ?? []).find((a) => a.id === entry.title) ?? null;
    setEditing({ initial: cfg });
  };

  /**
   * Abbrechen eines laufenden oder wartenden Jobs — die einzige Stelle, an der
   * die Zeitachse in den Runner hineingreift. Stirbt er zwischen Poll und
   * Klick, bleibt die Antwort ruhig: ein Satz im Jetzt-Band, kein roter Balken
   * über der Seite und keine verschwundene Historie.
   */
  const cancelEntry = async (entry: ActivityEntry) => {
    if (!entry.jobId || cancelBusyKey) return;
    setCancelBusyKey(entry.key);
    setCancelError(null);
    try {
      await cancelJob(entry.jobId);
      try {
        setFresh(await getQueue());
      } catch {
        // Der Abbruch ist durch; nur das Sofort-Nachladen nicht. Der Poll holt es.
      }
    } catch (e: any) {
      setCancelError(`${entry.title}: ${e?.message ?? String(e)}`);
    } finally {
      setCancelBusyKey(null);
    }
  };

  const bands: ActivityBand[] = [
    {
      id: "upcoming",
      title: "Geplant",
      hint: timeline.nextUp
        ? `als Nächstes von selbst: ${timeline.nextUp.title} — ${timeline.nextUp.who}`
        : "was von selbst laufen wird",
      note: autorunError ? (
        <>
          Runner nicht erreichbar — geplante Autoruns lassen sich gerade nicht abrufen
          <span className="act-offline"> ({autorunError})</span>. Der Verlauf unten stammt
          aus dem Run-Journal und ist vollständig.
        </>
      ) : autoruns === null ? (
        "Autoruns werden geladen …"
      ) : (
        "Kein Autorun eingerichtet — nichts läuft von selbst."
      ),
      action: (
        <Button
          variant="tinted"
          size="s"
          onPress={() => setEditing({ initial: null })}
        >
          <Icon name={princeIcon("add")} size={14} />
          Neuer Autorun
        </Button>
      ),
      entries: timeline.upcoming,
    },
    {
      id: "current",
      title: "Jetzt",
      hint: "läuft und wartet (Poll alle 3 s) — laufende und wartende Jobs lassen sich abbrechen",
      message: cancelError ? `Abbrechen fehlgeschlagen — ${cancelError}` : undefined,
      note: queueError ? (
        <>
          Runner nicht erreichbar — laufende und wartende Jobs sind gerade nicht abrufbar
          <span className="act-offline"> ({queueError})</span>. Sobald er antwortet, füllt
          sich dieses Band von selbst.
        </>
      ) : queue === null ? (
        "Queue wird geladen …"
      ) : (
        "Gerade läuft und wartet nichts."
      ),
      entries: timeline.current,
    },
    {
      id: "past",
      title: "Verlauf",
      hint: "gelaufene Runs und frisch beendete Jobs, neueste zuerst",
      note: (
        <>
          Noch keine Runs erfasst. Lege JSON-Dateien unter
          <code> maco-meta/runs/&lt;RUN_ID&gt;.json </code> ab.
        </>
      ),
      entries: timeline.past,
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <Flex style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            {/* Der Ziel-Badge zu `mechanism:runs` stand hier neben dem Titel;
                er steht jetzt im Abschnitt „Mechanismen" auf
                `/umsetzungsgrad`. */}
            <h2 className="page-title">Aktivität</h2>
            <div className="page-subtitle">
              Eine Zeitachse aus drei Quellen: geplante Autoruns, laufende und wartende Jobs,
              gelaufene Runs. Je Zeile: wann, wer ausgelöst hat, und der Zustand in denselben
              neun Slots wie überall.
            </div>
          </div>
          <div className="act-actions">
            <Button variant="filled" onPress={() => nav("/runs/new")}>
              <Icon name={princeIcon("add")} size={16} />
              Neuer Run
            </Button>
          </div>
        </Flex>
        {/* Die Seite erklärt sich selbst — aber standardmäßig zugeklappt und
            innerhalb der Kopfzeile, nicht zwischen Kopf und Achse. Wer täglich
            damit arbeitet, will die Zeilen sofort sehen; wer zum ersten Mal
            hier ist, findet eine Zeile über der Achse. Die Slot-Legende hängt
            zusätzlich als `title` an den Kürzeln der Tabellenkopfzeile
            (`EXPLAINED_SLOTS`) und kostet dort gar keine Fläche. */}
        <ActivityGuide />
      </div>

      {editing && (
        <div className="act-editor">
          <div className="act-editor-head">
            <h3>{editing.initial ? `Autorun pflegen: ${editing.initial.id}` : "Neuer Autorun"}</h3>
            <Button variant="plain" onPress={() => setEditing(null)}>
              <Icon name={princeIcon("decline")} size={16} />
              Schließen
            </Button>
          </div>
          {/* Der Editor existiert bereits — hier wird er benutzt, nicht nachgebaut. */}
          <AutorunEditor
            initial={editing.initial}
            onSaved={() => { setEditing(null); loadAutoruns(); }}
          />
        </div>
      )}

      <ActivityStream
        bands={bands}
        now={now}
        onOpenEntry={openAutorun}
        onCancel={cancelEntry}
        cancelBusyKey={cancelBusyKey}
        // Antwortet der Runner gerade nicht, sind die gezeigten Jobs ein
        // Standbild vom letzten geglückten Poll — der Knopf griffe ins Leere.
        cancelDisabled={queueError != null}
      />
    </div>
  );
}
